import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: window.location.origin });

const planColors = {
  Trial: { bg: '#1e293b', border: '#475569', badge: '#64748b' },
  Basic: { bg: '#0c2a1a', border: '#22c55e', badge: '#22c55e' },
  Pro: { bg: '#0c1f3a', border: '#0ea5e9', badge: '#0ea5e9' },
  Enterprise: { bg: '#1e0a3c', border: '#a855f7', badge: '#a855f7' },
};

export default function Register() {
  // referral_signup_ui_v1: read ?r=CODE from URL or localStorage
  const [searchParams] = useSearchParams();
  useEffect(() => {
    let code = searchParams.get('r') || '';
    if (!code) {
      try { code = localStorage.getItem('referral_code') || ''; } catch (e) {}
    }
    code = (code || '').trim().toUpperCase();
    if (code) {
      fetch('/api/referrals/validate/' + encodeURIComponent(code))
        .then(r => r.json())
        .then(data => {
          if (data && data.valid) {
            setForm(f => ({ ...f, referral_code: code }));
          }
        }).catch(() => {});
    }
  }, [searchParams]);

  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [step, setStep] = useState(1); // 1=เลือกแพ็คเกจ, 2=กรอกข้อมูล, 3=สำเร็จ
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', business_type: '', referral_code: '' }) // referral_signup_ui_v1;
  const [loading, setLoading] = useState(false);
  // unique_v1: live check email + name availability
  const [avail, setAvail] = useState({ email: null, name: null }); // null = not checked, true = available, false = taken
  const [checking, setChecking] = useState({ email: false, name: false });
  const [botLink, setBotLink] = useState(null); /* bot_link_v1 M4 */
  const navigate = useNavigate();

  // unique_v1: debounced availability check
  useEffect(() => {
    const email = form.email.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setAvail(a => ({ ...a, email: null })); return;
    }
    setChecking(c => ({ ...c, email: true }));
    const id = setTimeout(async () => {
      try {
        const { data } = await API.get('/api/auth/check-availability', { params: { email } });
        setAvail(a => ({ ...a, email: data.email_available }));
      } catch { setAvail(a => ({ ...a, email: null })); }
      setChecking(c => ({ ...c, email: false }));
    }, 500);
    return () => clearTimeout(id);
  }, [form.email]);

  useEffect(() => {
    const name = form.name.trim();
    if (!name || name.length < 2) { setAvail(a => ({ ...a, name: null })); return; }
    setChecking(c => ({ ...c, name: true }));
    const id = setTimeout(async () => {
      try {
        const { data } = await API.get('/api/auth/check-availability', { params: { name } });
        setAvail(a => ({ ...a, name: data.name_available }));
      } catch { setAvail(a => ({ ...a, name: null })); }
      setChecking(c => ({ ...c, name: false }));
    }, 500);
    return () => clearTimeout(id);
  }, [form.name]);

  useEffect(() => {
    API.get('/api/packages').then(({ data }) => setPackages(data));
  }, []);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.password || !form.phone) {
      toast.error('กรุณากรอกข้อมูลให้ครบ'); return;
    }
    // unique_v1: block if email or name already taken
    if (avail.email === false) { toast.error('อีเมลนี้มีผู้ใช้งานแล้ว'); return; }
    if (avail.name === false) { toast.error('ชื่อร้าน/บริษัทนี้มีผู้ใช้แล้ว'); return; }
    setLoading(true);
    try {
      const regRes = await API.post('/api/auth/register', { ...form, package_id: selected?.id });
      /* bot_link_v1 M4 */
      if (regRes.data && regRes.data.bot_link_token) {
        setBotLink({ token: regRes.data.bot_link_token, username: regRes.data.bot_username });
      }
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.error || 'เกิดข้อผิดพลาด');
    }
    setLoading(false);
  };

  const featureLabel = (k) => ({
    bot_training: '🤖 Bot Training',
    kpi_dashboard: '📊 KPI Dashboard',
    quick_replies: '⚡ Quick Replies',
    multi_channel: '📡 Multi Channel'
  })[k] || k;

  return (
    <div style={{ minHeight:'100vh', background:'#0f172a', padding:'40px 20px' }}>
      <div style={{ maxWidth: step === 1 ? 900 : 480, margin:'0 auto' }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:40 }}>
          <h1 style={{ color:'#38bdf8', fontSize:28, fontWeight:700, margin:0 }}>💬 AUTO CHATS</h1>
          <p style={{ color:'#64748b', marginTop:8 }}>ระบบจัดการแชทอัตโนมัติสำหรับธุรกิจ</p>
        </div>

        {/* Step indicator */}
        <div style={{ display:'flex', justifyContent:'center', gap:8, marginBottom:40 }}>
          {['เลือกแพ็คเกจ','กรอกข้อมูล','รอการอนุมัติ'].map((s,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700,
                background: step > i+1 ? '#22c55e' : step === i+1 ? '#0ea5e9' : '#1e293b',
                color: step >= i+1 ? '#fff' : '#475569',
                border: `2px solid ${step >= i+1 ? 'transparent' : '#334155'}` }}>
                {step > i+1 ? '✓' : i+1}
              </div>
              <span style={{ fontSize:13, color: step >= i+1 ? '#f1f5f9' : '#475569' }}>{s}</span>
              {i < 2 && <div style={{ width:40, height:2, background: step > i+1 ? '#22c55e' : '#1e293b' }} />}
            </div>
          ))}
        </div>

        {/* Step 1 - เลือกแพ็คเกจ */}
        {step === 1 && (
          <div>
            <h2 style={{ color:'#f1f5f9', textAlign:'center', marginBottom:32, fontSize:20 }}>เลือกแพ็คเกจที่เหมาะกับคุณ</h2>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:20, marginBottom:32 }}>
              {packages.map(pkg => {
                const colors = planColors[pkg.name] || planColors.Basic;
                const isSelected = selected?.id === pkg.id;
                return (
                  <div key={pkg.id} onClick={() => setSelected(pkg)}
                    style={{ background: colors.bg, border:`2px solid ${isSelected ? colors.border : '#1e293b'}`,
                      borderRadius:16, padding:24, cursor:'pointer', transition:'all 0.2s',
                      transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                      boxShadow: isSelected ? `0 0 20px ${colors.border}44` : 'none' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                      <h3 style={{ margin:0, color:'#f1f5f9', fontSize:18, fontWeight:700 }}>{pkg.name}</h3>
                      <span style={{ background:colors.badge+'22', color:colors.badge, borderRadius:6, padding:'2px 10px', fontSize:11, fontWeight:700 }}>
                        {pkg.price > 0 ? `฿${parseInt(pkg.price).toLocaleString()}/เดือน` : 'ฟรี'}
                      </span>
                    </div>
                    <p style={{ color:'#94a3b8', fontSize:13, margin:'0 0 16px', minHeight:36 }}>{pkg.description}</p>
                    <div style={{ borderTop:`1px solid ${colors.border}44`, paddingTop:12, display:'flex', flexDirection:'column', gap:6 }}>
                      <div style={{ fontSize:12, color:'#94a3b8' }}>📡 Social: <span style={{ color:'#f1f5f9', fontWeight:600 }}>{pkg.max_channels} บัญชี</span></div>
                      <div style={{ fontSize:12, color:'#94a3b8' }}>👤 Admin: <span style={{ color:'#f1f5f9', fontWeight:600 }}>{pkg.max_agents} คน</span></div>
                      <div style={{ fontSize:12, color:'#94a3b8' }}>🤖 KB: <span style={{ color:'#f1f5f9', fontWeight:600 }}>{pkg.max_kb} หัวข้อ</span></div>
                      <div style={{ fontSize:12, color:'#94a3b8' }}>📅 ระยะเวลา: <span style={{ color:'#f1f5f9', fontWeight:600 }}>{pkg.duration_days} วัน</span></div>
                    </div>
                    {pkg.features && (
                      <div style={{ marginTop:12, display:'flex', flexWrap:'wrap', gap:4 }}>
                        {Object.entries(pkg.features).filter(([,v]) => v).map(([k]) => (
                          <span key={k} style={{ fontSize:10, background:'#ffffff11', color:'#94a3b8', borderRadius:4, padding:'2px 6px' }}>
                            {featureLabel(k)}
                          </span>
                        ))}
                      </div>
                    )}
                    {isSelected && (
                      <div style={{ marginTop:16, textAlign:'center', color:colors.border, fontWeight:700, fontSize:13 }}>✓ เลือกแพ็คเกจนี้</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ textAlign:'center' }}>
              <button onClick={() => selected && setStep(2)} disabled={!selected}
                style={{ background: selected ? '#0ea5e9' : '#334155', color:'#fff', border:'none', borderRadius:10, padding:'14px 48px', fontSize:16, fontWeight:700, cursor: selected ? 'pointer' : 'not-allowed' }}>
                ถัดไป →
              </button>
              <p style={{ color:'#475569', marginTop:16, fontSize:13 }}>
                มีบัญชีแล้ว? <Link to="/login" style={{ color:'#38bdf8' }}>เข้าสู่ระบบ</Link>
              </p>
            </div>
          </div>
        )}

        {/* Step 2 - กรอกข้อมูล */}
        {step === 2 && (
          <div style={{ background:'#1e293b', borderRadius:16, padding:36 }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
              <button onClick={() => setStep(1)} style={{ background:'none', border:'none', color:'#64748b', cursor:'pointer', fontSize:20 }}>←</button>
              <div>
                <h2 style={{ margin:0, color:'#f1f5f9', fontSize:18 }}>กรอกข้อมูลสมัคร</h2>
                <p style={{ margin:0, color:'#64748b', fontSize:13 }}>แพ็คเกจ: <span style={{ color:'#0ea5e9' }}>{selected?.name}</span></p>
              </div>
            </div>
            {[
              { key:'name', label:'ชื่อธุรกิจ / ร้านค้า *', placeholder:'เช่น ร้านค้า ABC', type:'text', uniq:'name' },
              { key:'email', label:'อีเมล *', placeholder:'example@email.com', type:'email', uniq:'email' },
              { key:'password', label:'รหัสผ่าน *', placeholder:'อย่างน้อย 8 ตัวอักษร', type:'password' },
              { key:'phone', label:'เบอร์โทรติดต่อ *', placeholder:'08x-xxx-xxxx', type:'tel' },
              { key:'business_type', label:'ประเภทธุรกิจ', placeholder:'เช่น ร้านอาหาร, คาสิโน, ร้านค้าออนไลน์', type:'text' },
            ].map(f => {
              // unique_v1: status indicator
              const isCheck = f.uniq && checking[f.uniq];
              const status = f.uniq ? avail[f.uniq] : null; // true=avail, false=taken, null=idle
              const borderColor = status === true ? '#22c55e' : status === false ? '#ef4444' : '#334155';
              const indicator = isCheck ? '⏳ กำลังตรวจสอบ...' :
                                status === true ? '✓ ใช้ได้' :
                                status === false ? (f.uniq === 'email' ? '✗ อีเมลนี้มีผู้ใช้งานแล้ว' : '✗ ชื่อนี้มีผู้ใช้แล้ว') : '';
              const indicatorColor = status === true ? '#22c55e' : status === false ? '#ef4444' : '#94a3b8';
              return (
                <div key={f.key} style={{ marginBottom:16 }}>
                  <label style={{ color:'#94a3b8', fontSize:13, display:'block', marginBottom:6 }}>{f.label}</label>
                  <input type={f.type} value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})}
                    placeholder={f.placeholder}
                    style={{ width:'100%', padding:'11px 14px', borderRadius:8, border:`1px solid ${borderColor}`, background:'#0f172a', color:'#fff', fontSize:14, boxSizing:'border-box', transition:'border-color 0.2s' }} />
                  {indicator && (
                    <div style={{ fontSize:12, color:indicatorColor, marginTop:4 }}>{indicator}</div>
                  )}
                </div>
              );
            })}
            {/* unique_v1: disable if email/name not available */}
            <button onClick={handleSubmit} disabled={loading || avail.email === false || avail.name === false || checking.email || checking.name}
              style={{ width:'100%', padding:'13px',
                background: (loading || avail.email === false || avail.name === false || checking.email || checking.name) ? '#475569' : '#0ea5e9',
                color:'#fff', border:'none', borderRadius:10, fontSize:15, fontWeight:700,
                cursor: (loading || avail.email === false || avail.name === false || checking.email || checking.name) ? 'not-allowed' : 'pointer',
                marginTop:8 }}>
              {loading ? 'กำลังส่งข้อมูล...' : '📩 ส่งคำขอสมัคร'}
            </button>
          </div>
        )}

        {/* Step 3 - สำเร็จ */}
        {step === 3 && (
          <div style={{ background:'#1e293b', borderRadius:16, padding:48, textAlign:'center' }}>
            <div style={{ fontSize:64, marginBottom:20 }}>🎉</div>
            <h2 style={{ color:'#f1f5f9', fontSize:22, marginBottom:12 }}>ส่งคำขอสมัครสำเร็จ!</h2>
            <p style={{ color:'#94a3b8', marginBottom:8 }}>แพ็คเกจที่เลือก: <span style={{ color:'#0ea5e9', fontWeight:700 }}>{selected?.name}</span></p>
            <p style={{ color:'#64748b', fontSize:14, lineHeight:1.8, marginBottom:32 }}>
              ทีมงานจะตรวจสอบและติดต่อกลับภายใน 24 ชั่วโมงค่ะ<br/>
              หลังจากได้รับการอนุมัติ คุณจะสามารถเข้าใช้งานระบบได้ทันที
            </p>
            {/* bot_link_v1 M4 */}
            {botLink && botLink.token && (
              <div style={{ background:'#0f172a', border:'2px solid #f59e0b', borderRadius:12, padding:20, marginBottom:24 }}>
                <div style={{ color:'#fbbf24', fontWeight:700, fontSize:15, marginBottom:8 }}>⚠️ ขั้นตอนสำคัญ — กดติดตามบอทก่อนใช้งาน</div>
                <p style={{ color:'#94a3b8', fontSize:13, lineHeight:1.7, marginBottom:16 }}>
                  กดปุ่มด้านล่างเพื่อเชื่อมต่อ Telegram — รับแจ้งเตือนสถานะบัญชีและการอนุมัติผ่านบอทนี้
                </p>
                <a href={'https://t.me/' + (botLink.username || 'aiautochat1_bot') + '?start=' + botLink.token} target="_blank" rel="noopener noreferrer"
                   style={{ display:'inline-block', background:'#22c55e', color:'#fff', borderRadius:10, padding:'12px 28px', textDecoration:'none', fontWeight:700, fontSize:15 }}>
                  📲 กดติดตามบอท
                </a>
              </div>
            )}
            <Link to="/login" style={{ background:'#0ea5e9', color:'#fff', borderRadius:10, padding:'12px 32px', textDecoration:'none', fontWeight:700, fontSize:15 }}>
              ไปหน้า Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
